import type React from "react";
import { useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import type { CrmNotificationRule } from "../../../hooks/use-crm-notification-settings-api";
import {
  useCrmNotificationSettings,
  useUpdateCrmNotificationRule,
  useUpdateCrmNotificationSettings,
} from "../../../hooks/use-crm-notification-settings-api";
import {
  useSlackChannels,
  useSlackIntegration,
} from "../../../hooks/use-slack-integration-api";
import { CrmNotificationSettingsPage } from "./CrmNotificationSettingsPage";

/**
 * CRM通知設定ページのコンテナコンポーネント
 * @return {React.ReactElement} CRM通知設定ページ
 */
const CrmNotificationSettingsPageContainer: React.FC = () => {
  const {
    settings,
    loading: settingsLoading,
    refetch,
  } = useCrmNotificationSettings();
  const { integration: slackIntegration, loading: slackLoading } =
    useSlackIntegration();
  const {
    channels,
    loading: channelsLoading,
    fetchChannels,
  } = useSlackChannels();
  const { updateSettings, loading: updatingSettings } =
    useUpdateCrmNotificationSettings();
  const { updateRule, loading: updatingRule } = useUpdateCrmNotificationRule();

  // Slack連携済みの場合のみチャンネル一覧を取得
  useEffect(() => {
    if (slackIntegration?.isConnected) {
      fetchChannels().catch(() => undefined);
    }
  }, [slackIntegration?.isConnected, fetchChannels]);

  /**
   * Slack通知マスタートグル変更ハンドラ
   * @param {boolean} enabled 有効/無効
   * @return {void}
   */
  const handleToggleSlack = useCallback(
    (enabled: boolean): void => {
      updateSettings({ slackEnabled: enabled })
        .then(() => {
          toast.success(
            enabled ? "Slack通知を有効にしました" : "Slack通知を無効にしました",
          );
          return refetch();
        })
        .catch(() => {
          toast.error("通知設定の更新に失敗しました");
        });
    },
    [updateSettings, refetch],
  );

  /**
   * ルール更新ハンドラ
   * @param {string} ruleId ルールID
   * @param {object} data 更新データ
   * @return {void}
   */
  const handleUpdateRule = useCallback(
    (
      ruleId: string,
      data: Partial<
        Pick<
          CrmNotificationRule,
          "isEnabled" | "slackChannelId" | "slackChannelName"
        >
      >,
    ): void => {
      updateRule(ruleId, data)
        .then(() => {
          toast.success("通知ルールを更新しました");
          return refetch();
        })
        .catch(() => {
          toast.error("通知ルールの更新に失敗しました");
        });
    },
    [updateRule, refetch],
  );

  return (
    <CrmNotificationSettingsPage
      settings={settings}
      settingsLoading={settingsLoading}
      slackIntegration={slackIntegration}
      slackLoading={slackLoading}
      channels={channels}
      channelsLoading={channelsLoading}
      updatingSettings={updatingSettings}
      updatingRule={updatingRule}
      onToggleSlack={handleToggleSlack}
      onUpdateRule={handleUpdateRule}
    />
  );
};

export default CrmNotificationSettingsPageContainer;
